/**
 * Additional-information fields for preferred / non-preferred drug context.
 * @module AdditionalInformationSection
 */

import {
  InputField,
  MultiSelectInput,
} from "@risalabs_frontend_org/oasis-ui-kit";
import { ChangeEvent, useMemo } from "react";
import PreferredJcodesInput from "./PreferredJcodesInput";
import { AdditionalInformationSectionProps } from "./statusUpdateModal.types";
import { buildPreferredDrugOptions } from "./utils/buildPreferredDrugOptions";

/**
 * Renders preferred J-code rows, step-therapy count, tried/failed multi-select,
 * and source fields for the Status Update modal. When
 * `isUserInputRequired` is true, preferred J-code rows, step-therapy count,
 * and source show as required. Selecting ``none`` in tried/failed replaces
 * any other preferred-drug selections.
 *
 * @input props - Controlled preferred-drug state, portal target, and
 *   required-field flag
 * @return JSX for the additional-information section
 */
const AdditionalInformationSection = ({
  preferredDrugForm,
  onPreferredJcodeEntriesChange,
  setPreferredDrugValues,
  setStepTherapyCount,
  setSource,
  targetRef,
  isUserInputRequired,
}: AdditionalInformationSectionProps) => {
  const {
    preferredJcodeEntries,
    preferredDrugValues,
    stepTherapyCount,
    source,
  } = preferredDrugForm;

  const preferredDrugOptions = useMemo(
    () => buildPreferredDrugOptions(preferredJcodeEntries),
    [preferredJcodeEntries],
  );

  /**
   * Applies tried/failed multi-select values.
   *
   * Choosing ``none`` clears every other selection so it cannot be combined
   * with specific preferred drugs.
   *
   * @input values - Selected option values from the multi-select
   */
  const handlePreferredDrugValuesChange = (values: string[]) => {
    if (values.includes("none")) {
      setPreferredDrugValues(["none"]);
      return;
    }
    setPreferredDrugValues(values);
  };

  return (
    <div className="additonal-information--container flex flex-col gap-3">
      <PreferredJcodesInput
        entries={preferredJcodeEntries}
        onEntriesChange={onPreferredJcodeEntriesChange}
        required={isUserInputRequired ?? false}
      />
      <InputField
        id="preferred-step-therapy-count"
        label="Number of preferred drugs that must be tried & failed"
        required={isUserInputRequired ?? false}
        fullWidth
        value={stepTherapyCount}
        onChange={(event: ChangeEvent<HTMLInputElement>) => {
          setStepTherapyCount(event.target.value);
        }}
        type="number"
      />
      <MultiSelectInput
        label="Preferred drug(s) already tried / failed"
        placeholder="Select Preferred Tried / Failed"
        options={preferredDrugOptions}
        value={preferredDrugValues}
        onChange={handlePreferredDrugValuesChange}
        targetRef={targetRef}
        className="w-full"
      />
      <InputField
        id="preferred-drug-source"
        label="Source"
        required={isUserInputRequired ?? false}
        fullWidth
        placeholder="Enter Source"
        value={source}
        onChange={(event: ChangeEvent<HTMLInputElement>) => {
          setSource(event.target.value);
        }}
      />
    </div>
  );
};

export default AdditionalInformationSection;
